import { COURAGES, LIEUX, TEMPS_DE_J_AI } from '@nextilt/commun';
import type { CourageDeMaintenant, LieuDeMaintenant, TempsDeJAi } from '../partage/api/types';

/**
 * Les réglages de « Maintenant » (chantier flottaison, ticket 04) : combien de temps tu as
 * (« J'ai »), où tu es (« Je suis ») et ton courage. Le serveur filtre et classe avec eux
 * (`/api/maintenant`) ; le client les garde sur l'appareil d'une visite à l'autre (décision
 * d'Igor du 2026-10-01, choix A), pas dans le compte.
 *
 * Le rangement de l'appareil peut manquer ou refuser (navigation privée, données bloquées) :
 * on le lit et on l'écrit dans un `try`, et la page s'ouvre alors sur les défauts.
 */

export type Lieu = LieuDeMaintenant;
export type Courage = CourageDeMaintenant;

export interface ReglagesDeMaintenant {
  /** `null` : tout le temps. */
  temps: TempsDeJAi | null;
  /** `null` : où que tu sois. */
  lieu: Lieu | null;
  courage: Courage;
}

export const REGLAGES_PAR_DEFAUT: ReglagesDeMaintenant = { temps: null, lieu: null, courage: 'normal' };

export const CLE_DES_REGLAGES = 'nextilt.maintenant.reglages';

/** Ce qu'il faut du rangement de l'appareil (`localStorage`). */
export interface Rangement {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
}

/** Une valeur gardée qui n'est pas dans la liste retombe sur son défaut. */
function parmi<T extends string>(valeur: unknown, liste: readonly T[]): T | null {
  return liste.includes(valeur as T) ? (valeur as T) : null;
}

/** Les réglages gardés sur l'appareil, ou les défauts. Ne lève jamais. */
export function lireLesReglages(rangement: () => Rangement | null | undefined): ReglagesDeMaintenant {
  try {
    const texte = rangement()?.getItem(CLE_DES_REGLAGES);
    if (!texte) return REGLAGES_PAR_DEFAUT;
    const lu: unknown = JSON.parse(texte);
    if (typeof lu !== 'object' || lu === null) return REGLAGES_PAR_DEFAUT;
    const { temps, lieu, courage } = lu as Record<string, unknown>;
    return {
      temps: parmi(temps, TEMPS_DE_J_AI),
      lieu: parmi(lieu, LIEUX),
      courage: parmi(courage, COURAGES) ?? REGLAGES_PAR_DEFAUT.courage,
    };
  } catch {
    return REGLAGES_PAR_DEFAUT;
  }
}

/** Garde les réglages sur l'appareil. Un rangement qui refuse ne fait rien d'autre que ne pas garder. */
export function ecrireLesReglages(rangement: () => Rangement | null | undefined, reglages: ReglagesDeMaintenant): void {
  try {
    rangement()?.setItem(CLE_DES_REGLAGES, JSON.stringify(reglages));
  } catch {
    // Rien : la page marche, elle se rouvrira simplement sur les défauts.
  }
}

/** La requête de `/api/maintenant` : les réglages posés, le projet choisi, « Voir le reste ». */
export function requeteDesReglages(reglages: ReglagesDeMaintenant, projet: string | null, toutVoir: boolean): string {
  const p = new URLSearchParams();
  if (reglages.temps) p.set('temps', reglages.temps);
  if (reglages.lieu) p.set('lieu', reglages.lieu);
  p.set('courage', reglages.courage);
  if (projet) p.set('projet', projet);
  if (toutVoir) p.set('toutVoir', 'true');
  return p.toString();
}
